import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FixedSizeList as List } from 'react-window';

import { Box } from '@atlaskit/primitives/compiled/box';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import type { Snippet } from '../types/snippet.ts';

import { useSnippetPopupPreview } from './preview/SnippetPopupPreviewContext.tsx';
import { SnippetPanelItem } from './snippet-panel-item/SnippetPanelItem.tsx';
import { SNIPPET_CARD_HEIGHT } from './snippet-panel-item/SnippetPanelCard.tsx';

const styles = cssMap({
	container: {
		flexGrow: 1,
		minHeight: '0px',
		marginInline: token('space.negative.200'),
		overflow: 'hidden',
	},
});

export const useVirtualizedListHeight = (
	containerRef: React.RefObject<HTMLDivElement | null>,
): { containerHeight: number } => {
	const [containerHeight, setContainerHeight] = useState(0);

	useEffect(() => {
		const node = containerRef.current;
		if (!node) {
			return;
		}

		const observer = new ResizeObserver((entries) => {
			const entry = entries[0];
			if (entry) {
				setContainerHeight(entry.contentRect.height);
			}
		});

		observer.observe(node);
		return () => observer.disconnect();
	}, [containerRef]);

	return { containerHeight };
};

type SnippetPanelListProps = {
	snippets: Snippet[];
	contentId: string;
	spaceId: string;
	spaceKey: string;
};

type SnippetPanelListItemData = SnippetPanelListProps;

type SnippetPanelListRowProps = {
	data: SnippetPanelListItemData;
	index: number;
	style: React.CSSProperties;
};

const SnippetPanelListRow = ({
	data: { snippets, contentId, spaceId, spaceKey },
	index,
	style,
}: SnippetPanelListRowProps) => (
	<div style={style} role="listitem">
		<SnippetPanelItem
			snippet={snippets[index]}
			contentId={contentId}
			spaceId={spaceId}
			spaceKey={spaceKey}
		/>
	</div>
);

const getItemKey = (index: number, data: SnippetPanelListItemData) =>
	data.snippets[index].snippetId;

export const SnippetPanelList = ({
	snippets,
	contentId,
	spaceId,
	spaceKey,
}: SnippetPanelListProps): React.JSX.Element => {
	const { hidePreview } = useSnippetPopupPreview();

	const containerRef = useRef<HTMLDivElement>(null);
	const listRef = useRef<List>(null);

	const { containerHeight } = useVirtualizedListHeight(containerRef);
	const snippetIds = useMemo(
		() =>
			snippets
				.map((snippet) => snippet.snippetId)
				.sort()
				.join(','),
		[snippets],
	);
	useEffect(() => {
		// Reset only for a different set of snippets and retain the current position when insertion
		// updates usage count (and potentially popularity order).
		listRef.current?.scrollTo(0);
	}, [snippetIds]);

	const itemCount = snippets.length;
	const itemData = useMemo(
		() => ({
			snippets,
			contentId,
			spaceId,
			spaceKey,
		}),
		[snippets, contentId, spaceId, spaceKey],
	);

	return (
		<Box xcss={styles.container} ref={containerRef} onMouseLeave={hidePreview}>
			{containerHeight > 0 && (
				<List
					ref={listRef}
					height={containerHeight}
					itemCount={itemCount}
					itemData={itemData}
					itemSize={SNIPPET_CARD_HEIGHT}
					width="100%"
					itemKey={getItemKey}
					overscanCount={3}
				>
					{SnippetPanelListRow}
				</List>
			)}
		</Box>
	);
};
